import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Plus, Pencil, Archive, Trash2, X, Loader2, Building2, CreditCard, PiggyBank, MoreHorizontal, Check, Star } from "lucide-react";
import api, { fmtMoney, formatApiErrorDetail } from "@/lib/api";

const ACCOUNT_TYPES = [
  { value: "checking", label: "Checking", icon: Building2 },
  { value: "savings", label: "Savings", icon: PiggyBank },
  { value: "credit_card", label: "Credit card", icon: CreditCard },
  { value: "loan", label: "Loan", icon: MoreHorizontal },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

const TYPE_TONES = {
  checking:    { bg: "bg-forest/10", label: "text-forest" },
  savings:     { bg: "bg-sky-100",   label: "text-sky-700" },
  credit_card: { bg: "bg-plum-100 bg-violet-100", label: "text-violet-700" },
  loan:        { bg: "bg-rose-100",  label: "text-rose-700" },
  other:       { bg: "bg-slate-100", label: "text-slate-700" },
};

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank-accounts", { params: { include_archived: includeArchived } });
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [includeArchived]);

  const total = accounts
    .filter((a) => !a.is_archived)
    .reduce((sum, a) => sum + (a.current_balance || 0), 0);

  const setPrimary = async (a) => {
    try {
      await api.patch(`/bank-accounts/${a.id}`, { is_primary: true });
      toast.success(`${a.name} is now your primary account.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (a) => {
    const msg = a.transaction_count > 0
      ? `Archive "${a.name}"? It has ${a.transaction_count} linked transaction(s), so it will be soft-archived (transactions stay intact).`
      : `Delete "${a.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/bank-accounts/${a.id}`);
      toast.success(data.hard ? "Account deleted." : `Account archived. ${data.transactions_kept} transactions kept.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const unarchive = async (a) => {
    try {
      await api.patch(`/bank-accounts/${a.id}`, { is_archived: false });
      toast.success("Account restored.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="bank-accounts-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-1.5">
            <Landmark className="w-3 h-3" strokeWidth={2} /> Bank accounts
          </div>
          <h1 className="font-heading text-3xl font-medium tracking-tight mt-1">
            Where your money lives
          </h1>
          <p className="text-sm text-ink-light mt-1.5">
            Tag transactions and statement uploads by the real-world account they came from.
            Balances update automatically as transactions are added.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-forest inline-flex items-center gap-1.5 text-sm"
          data-testid="add-account-button"
        >
          <Plus className="w-4 h-4" /> Add account
        </button>
      </div>

      {/* Total tile */}
      {!loading && accounts.filter(a => !a.is_archived).length > 0 && (
        <div className="rounded-2xl bg-forest text-white p-6 flex items-end justify-between" data-testid="total-cash-tile">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-forest-100">Total across all active accounts</div>
            <div className="font-heading text-4xl font-medium num mt-1">{fmtMoney(total)}</div>
          </div>
          <div className="text-xs text-forest-100">
            {accounts.filter(a => !a.is_archived).length} account{accounts.filter(a => !a.is_archived).length === 1 ? "" : "s"}
          </div>
        </div>
      )}

      {/* Accounts grid */}
      {loading ? (
        <div className="card-surface p-8 flex items-center justify-center text-ink-lighter text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading accounts…
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState onAdd={() => setShowAdd(true)} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {accounts.map((a) => (
            <AccountCard
              key={a.id}
              account={a}
              onEdit={() => setEditing(a)}
              onDelete={() => remove(a)}
              onUnarchive={() => unarchive(a)}
              onSetPrimary={() => setPrimary(a)}
            />
          ))}
        </div>
      )}

      {/* Archive toggle */}
      {!loading && accounts.some(a => a.is_archived) && (
        <label className="inline-flex items-center gap-2 text-xs text-ink-light cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="accent-forest"
            data-testid="show-archived-toggle"
          />
          Show archived
        </label>
      )}
      {!accounts.some(a => a.is_archived) && !loading && (
        <label className="inline-flex items-center gap-2 text-xs text-ink-lighter cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="accent-forest"
            data-testid="show-archived-toggle"
          />
          Show archived
        </label>
      )}

      {(showAdd || editing) && (
        <AccountModal
          account={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AccountCard({ account, onEdit, onDelete, onUnarchive, onSetPrimary }) {
  const typeMeta = ACCOUNT_TYPES.find((t) => t.value === account.type) || ACCOUNT_TYPES[4];
  const tone = TYPE_TONES[account.type] || TYPE_TONES.other;
  const TypeIcon = typeMeta.icon;
  const isNegative = account.current_balance < 0;

  return (
    <div
      className={`card-surface p-5 ${account.is_archived ? "opacity-60" : ""}`}
      data-testid={`account-card-${account.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg ${tone.bg} ${tone.label} flex items-center justify-center shrink-0`}>
            <TypeIcon className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-medium truncate">{account.name}</div>
              {account.is_primary && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-forest/10 text-forest text-[10px] font-medium px-1.5 py-0.5">
                  <Star className="w-2.5 h-2.5" fill="currentColor" strokeWidth={0} /> Primary
                </span>
              )}
              {account.is_archived && (
                <span className="rounded-full bg-ink-lighter/15 text-ink-lighter text-[10px] font-medium px-1.5 py-0.5">
                  Archived
                </span>
              )}
            </div>
            <div className="text-xs text-ink-lighter mt-0.5">
              {typeMeta.label}
              {account.institution && ` · ${account.institution}`}
              {account.mask && ` · ••${account.mask}`}
            </div>
          </div>
        </div>
      </div>

      <div className={`font-heading text-3xl font-medium num ${isNegative ? "text-ochre" : "text-ink"}`} data-testid={`account-balance-${account.id}`}>
        {fmtMoney(account.current_balance)}
      </div>
      <div className="text-xs text-ink-lighter mt-1">
        Opening {fmtMoney(account.opening_balance)} on {account.opening_date}
        {" · "}
        {account.transaction_count} txn{account.transaction_count === 1 ? "" : "s"}
      </div>

      <div className="mt-4 pt-4 border-t border-border flex items-center gap-1">
        {!account.is_primary && !account.is_archived && (
          <button
            onClick={onSetPrimary}
            className="text-xs text-ink-lighter hover:text-forest inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-forest/5"
            data-testid={`account-set-primary-${account.id}`}
          >
            <Star className="w-3 h-3" strokeWidth={1.75} /> Make primary
          </button>
        )}
        <div className="ml-auto flex items-center gap-1">
          {account.is_archived ? (
            <button
              onClick={onUnarchive}
              className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
              title="Restore"
              data-testid={`account-restore-${account.id}`}
            >
              <Archive className="w-4 h-4" strokeWidth={1.75} />
            </button>
          ) : (
            <button
              onClick={onEdit}
              className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
              title="Edit"
              data-testid={`account-edit-${account.id}`}
            >
              <Pencil className="w-4 h-4" strokeWidth={1.75} />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-1.5 rounded-md text-ink-lighter hover:text-destructive hover:bg-destructive/5"
            title={account.transaction_count > 0 ? "Archive" : "Delete"}
            data-testid={`account-delete-${account.id}`}
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="card-surface p-10 text-center" data-testid="accounts-empty">
      <div className="w-14 h-14 rounded-full bg-forest/10 text-forest flex items-center justify-center mx-auto mb-4">
        <Landmark className="w-6 h-6" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-xl font-medium">No bank accounts yet</div>
      <p className="text-sm text-ink-light mt-2 max-w-md mx-auto">
        Add your first account (Checking, Savings, or Credit card) so you can tag transactions and see your cash across the whole business.
      </p>
      <button onClick={onAdd} className="btn-forest mt-5 inline-flex items-center gap-1.5" data-testid="empty-add-account">
        <Plus className="w-4 h-4" /> Add your first account
      </button>
    </div>
  );
}

function AccountModal({ account, onClose, onSaved }) {
  const isEdit = Boolean(account);
  const [form, setForm] = useState({
    name: account?.name || "",
    type: account?.type || "checking",
    institution: account?.institution || "",
    mask: account?.mask || "",
    opening_balance: account?.opening_balance ?? 0,
    opening_date: account?.opening_date || new Date().toISOString().slice(0, 10),
    is_primary: account?.is_primary || false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_balance: parseFloat(form.opening_balance) || 0,
        institution: form.institution.trim() || null,
        mask: form.mask.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/bank-accounts/${account.id}`, payload);
        toast.success("Account updated.");
      } else {
        await api.post("/bank-accounts", payload);
        toast.success("Account added.");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="account-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline mb-1">{isEdit ? "Edit" : "Add"} bank account</div>
            <div className="font-heading text-xl font-medium">
              {isEdit ? account.name : "Where does the money live?"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-ink-lighter hover:text-ink hover:bg-paper"
            data-testid="account-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs text-ink-light mb-1">Account name</span>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Chase Business Checking"
              className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest"
              data-testid="account-input-name"
            />
          </label>

          <label className="block">
            <span className="block text-xs text-ink-light mb-1">Type</span>
            <div className="grid grid-cols-5 gap-1.5" data-testid="account-type-picker">
              {ACCOUNT_TYPES.map((t) => {
                const Icon = t.icon;
                const active = form.type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, type: t.value }))}
                    className={`rounded-md border px-2 py-2 text-[11px] flex flex-col items-center gap-1 transition-colors ${
                      active
                        ? "border-forest bg-forest/5 text-forest"
                        : "border-border text-ink-light hover:border-ink-lighter"
                    }`}
                    data-testid={`account-type-${t.value}`}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-ink-light mb-1">Institution (optional)</span>
              <input
                type="text"
                value={form.institution}
                onChange={(e) => setForm((f) => ({ ...f, institution: e.target.value }))}
                placeholder="Chase"
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest"
                data-testid="account-input-institution"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-ink-light mb-1">Last 4 digits (optional)</span>
              <input
                type="text"
                value={form.mask}
                maxLength={8}
                onChange={(e) => setForm((f) => ({ ...f, mask: e.target.value.replace(/\D/g, "") }))}
                placeholder="1234"
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest num"
                data-testid="account-input-mask"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-ink-light mb-1">Opening balance</span>
              <input
                type="number"
                step="0.01"
                value={form.opening_balance}
                onChange={(e) => setForm((f) => ({ ...f, opening_balance: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest num"
                data-testid="account-input-opening-balance"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-ink-light mb-1">Opening date</span>
              <input
                type="date"
                required
                value={form.opening_date}
                onChange={(e) => setForm((f) => ({ ...f, opening_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest"
                data-testid="account-input-opening-date"
              />
            </label>
          </div>

          <label className="flex items-center gap-2 mt-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked }))}
              className="accent-forest"
              data-testid="account-input-primary"
            />
            <span className="text-sm text-ink">Make this my primary account</span>
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm text-ink-light hover:text-ink" data-testid="account-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-forest text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="account-save">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add account"}
          </button>
        </div>
      </form>
    </div>
  );
}
